import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Select,
  Spinner,
  notify,
} from '@aeris/ui'

import {
  categorizeProductsByName,
  fetchNameRules,
  fetchProductCategories,
  saveNameRules,
} from '../lib/api/product-categories.ts'
import { ApiError } from '../lib/api.ts'
import { categoryLabel } from '../lib/category-label.ts'

/**
 * Classificar pelo nome — F163, F272.
 *
 * O catálogo da loja veio do sistema antigo sem categoria nenhuma: lá ela era
 * o nome do arquivo exportado. O que sobrou foi o nome da peça, que a carrega
 * — `f.` é tela, `bat.` é bateria. Marcar 1.205 telas à mão não acontece.
 *
 * ## O que a F272 acrescentou
 *
 * O dono: "toda vez some, tenho que botar de novo… botei errado". As regras
 * ficam GRAVADAS (abrem como foram usadas da última vez), cada uma escolhe
 * "começa com" ou "contém", e pode trocar também quem já tem outra categoria —
 * para consertar marcação errada. A ordem importa: a primeira que casa ganha.
 *
 * ## Por que a prévia é obrigatória
 *
 * Uma regra de nome é um chute sobre dez anos de cadastro digitado por pessoas
 * diferentes. O botão de gravar só aparece DEPOIS de a prévia dizer quantos
 * produtos cada regra pega — e quantos trocam de categoria — pelo mesmo
 * caminho da gravação, sem gravar.
 */

type Match = 'prefix' | 'contains'
type Rule = {
  pattern: string
  match: Match
  categoryId: string
  overwrite: boolean
}

const DEFAULT_RULES: Rule[] = [
  { pattern: 'f.', match: 'prefix', categoryId: '', overwrite: false },
  { pattern: 'bat.', match: 'prefix', categoryId: '', overwrite: false },
]

const MATCH_LABEL: Record<Match, string> = {
  prefix: 'Começa com',
  contains: 'Contém',
}

export type CategorizeProductsDialogProps = {
  onClose: () => void
  /** Chamado depois de gravar, para a lista se recarregar. */
  onDone: () => void
}

export const CategorizeProductsDialog = ({
  onClose,
  onDone,
}: CategorizeProductsDialogProps): ReactNode => {
  const queryClient = useQueryClient()
  const categories = useQuery({
    queryKey: ['product-categories', 'active'],
    queryFn: async ({ signal }) =>
      await fetchProductCategories('active', signal),
  })
  const saved = useQuery({
    queryKey: ['product-category-name-rules'],
    queryFn: async ({ signal }) => await fetchNameRules(signal),
  })

  /** O rascunho: nulo até a pessoa mexer; até lá vale o que está gravado. */
  const [draft, setDraft] = useState<Rule[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const rules: Rule[] =
    draft ??
    (saved.data !== undefined && saved.data.length > 0
      ? saved.data
      : DEFAULT_RULES)

  const usable = rules.filter(
    (rule) => rule.pattern.trim() !== '' && rule.categoryId !== ''
  )

  const failure = (fallback: string) => (cause: unknown) => {
    setError(cause instanceof ApiError ? cause.message : fallback)
  }

  const preview = useMutation({
    mutationFn: async () =>
      await categorizeProductsByName({ rules: usable, dryRun: true }),
    onError: failure('Não foi possível montar a prévia.'),
    onSuccess: () => {
      setError(null)
    },
  })

  const apply = useMutation({
    mutationFn: async () =>
      await categorizeProductsByName({ rules: usable, dryRun: false }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['product-categories'] })
      await queryClient.invalidateQueries({
        queryKey: ['product-category-name-rules'],
      })
      onDone()
      onClose()
    },
    onError: failure('Não foi possível gravar as categorias.'),
  })

  const keep = useMutation({
    mutationFn: async () => await saveNameRules(usable),
    onSuccess: (stored) => {
      queryClient.setQueryData(['product-category-name-rules'], stored)
      setDraft(null)
      setError(null)
      notify.success('Regras salvas. Elas abrem assim da próxima vez.')
    },
    onError: failure('Não foi possível salvar as regras.'),
  })

  const change = (next: Rule[]): void => {
    setDraft(next)
    preview.reset()
  }

  const update = (index: number, patch: Partial<Rule>): void => {
    change(
      rules.map((rule, position) =>
        position === index ? { ...rule, ...patch } : rule
      )
    )
  }

  const move = (index: number, delta: number): void => {
    const next = [...rules]
    const a = next[index]
    const b = next[index + delta]
    if (a === undefined || b === undefined) return
    next[index] = b
    next[index + delta] = a
    change(next)
  }

  const changing =
    preview.data?.rules.reduce((total, rule) => total + rule.changed, 0) ?? 0

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Classificar pelo nome"
        description="Marca a categoria pelo nome do produto. Sem “Trocar quem já tem categoria”, só os que ainda não têm nenhuma mudam. A primeira regra que casa ganha, e as regras ficam guardadas para a próxima vez."
        className="max-w-3xl"
      >
        <div className="flex flex-col gap-3">
          {saved.isPending ? <Spinner /> : null}

          {rules.map((rule, index) => (
            <div
              key={String(index)}
              className="flex flex-col gap-2 border-b border-(--border-subtle) pb-3"
            >
              <div className="flex flex-wrap items-end gap-2">
                <Field label="Como">
                  {(field) => (
                    <Select
                      id={field.id}
                      value={rule.match}
                      className="w-36"
                      onChange={(event) => {
                        update(index, { match: event.target.value as Match })
                      }}
                    >
                      <option value="prefix">{MATCH_LABEL.prefix}</option>
                      <option value="contains">{MATCH_LABEL.contains}</option>
                    </Select>
                  )}
                </Field>
                <Field label="Texto do nome">
                  {(field) => (
                    <Input
                      id={field.id}
                      value={rule.pattern}
                      className="w-44"
                      onChange={(event) => {
                        update(index, { pattern: event.target.value })
                      }}
                    />
                  )}
                </Field>
                <Field label="Categoria">
                  {(field) => (
                    <Select
                      id={field.id}
                      value={rule.categoryId}
                      className="w-56"
                      onChange={(event) => {
                        update(index, { categoryId: event.target.value })
                      }}
                    >
                      <option value="">Escolha uma categoria</option>
                      {(categories.data ?? []).map((category) => (
                        <option key={category.id} value={category.id}>
                          {categoryLabel(category)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Subir a regra ${String(index + 1)}`}
                  disabled={index === 0}
                  onClick={() => {
                    move(index, -1)
                  }}
                >
                  <ArrowUp aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Descer a regra ${String(index + 1)}`}
                  disabled={index === rules.length - 1}
                  onClick={() => {
                    move(index, 1)
                  }}
                >
                  <ArrowDown aria-hidden="true" className="h-4 w-4" />
                </Button>
                {rules.length === 1 ? null : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      change(rules.filter((_, position) => position !== index))
                    }}
                  >
                    Tirar
                  </Button>
                )}
              </div>
              <CheckboxField
                label="Trocar também quem já tem outra categoria"
                checked={rule.overwrite}
                onCheckedChange={(checked) => {
                  update(index, { overwrite: checked === true })
                }}
              />
            </div>
          ))}

          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                change([
                  ...rules,
                  {
                    pattern: '',
                    match: 'contains',
                    categoryId: '',
                    overwrite: false,
                  },
                ])
              }}
            >
              Mais uma regra
            </Button>
          </div>

          {categories.data?.length === 0 ? (
            <InlineAlert tone="warning">
              Nenhuma categoria cadastrada ainda. Crie uma no cadastro de um
              produto e volte aqui.
            </InlineAlert>
          ) : null}

          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          {preview.data === undefined ? null : (
            <div className="flex flex-col gap-1">
              {preview.data.rules.map((rule, index) => (
                <p
                  key={`${String(index)}-${rule.pattern}`}
                  className="text-(length:--text-sm) text-(--text-secondary)"
                >
                  {MATCH_LABEL[rule.match].toLowerCase()}{' '}
                  <strong>{rule.pattern}</strong> → {rule.categoryName}:{' '}
                  {String(rule.matched)}{' '}
                  {rule.matched === 1 ? 'produto' : 'produtos'}
                  {rule.changed === 0
                    ? ''
                    : ` (${String(rule.changed)} ${rule.changed === 1 ? 'troca' : 'trocam'} de categoria)`}
                </p>
              ))}
              <p className="text-(length:--text-sm) text-(--text-muted)">
                {String(preview.data.remaining)}{' '}
                {preview.data.remaining === 1
                  ? 'produto fica'
                  : 'produtos ficam'}{' '}
                sem categoria.
              </p>
              {changing === 0 ? null : (
                <InlineAlert tone="warning">
                  {`${String(changing)} ${changing === 1 ? 'produto que já tem categoria vai trocar' : 'produtos que já têm categoria vão trocar'}. Confira antes de marcar.`}
                </InlineAlert>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="secondary"
            disabled={usable.length === 0 || keep.isPending}
            onClick={() => {
              keep.mutate()
            }}
          >
            {keep.isPending ? <Spinner /> : 'Salvar regras'}
          </Button>
          {preview.data === undefined ? (
            <Button
              variant="primary"
              disabled={usable.length === 0 || preview.isPending}
              onClick={() => {
                preview.mutate()
              }}
            >
              {preview.isPending ? <Spinner /> : 'Ver a prévia'}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={apply.isPending}
              onClick={() => {
                apply.mutate()
              }}
            >
              {apply.isPending ? <Spinner /> : 'Marcar os produtos'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
